import React, { useEffect, useState } from "react";
import client, { apiErr, API } from "../lib/api";
import { Button, Card, Badge, Modal } from "../components/ui";
import { Mail, MessageSquare, Save, Send, CheckCircle2, AlertTriangle, CreditCard, Bell, Languages, Loader2, Download, Lock, FileCheck, Upload, ExternalLink, X, RefreshCw, Eye, GitCompare } from "lucide-react";
import { LANGS } from "../i18n";
import SaasSignableSetup from "../components/SaasSignableSetup";
import { MaintenanceNotice } from "../components/MaintenanceNotice";

function Field({ label, value, onChange, placeholder, info, type = "text", testid }) {
  return (
    <label className="block">
      <span className="mb-1 flex items-center gap-2 text-xs font-medium opacity-70">{label}
        {info?.set && <Badge color={info.source === "custom" ? "info" : "neutral"}>{info.source === "custom" ? "custom" : "env"} · {info.masked}</Badge>}
      </span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm outline-none focus:border-[var(--primary)]" />
    </label>
  );
}

function LegalMetaControls({ docKey, meta, onMetaSaved }) {
  const [ver, setVer] = useState("1.0.0");
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  useEffect(() => {
    setVer(meta?.version || "1.0.0");
    setDate((meta?.last_modified || "").slice(0, 10));
  }, [meta?.version, meta?.last_modified]);
  const save = async (v, d) => {
    setBusy(true); setNote("");
    try {
      await client.put(`/admin/legal-meta/${docKey}`, { version: v, last_modified: d });
      onMetaSaved?.(docKey, { version: v, last_modified: d });
      setNote("✓ Saved"); setTimeout(() => setNote(""), 2000);
    } catch (e) { setNote(apiErr(e)); }
    finally { setBusy(false); }
  };
  const bump = () => {
    const parts = (ver || "1.0.0").split(".").map((p) => parseInt(p, 10) || 0);
    while (parts.length < 3) parts.push(0);
    parts[2] += 1;
    const nv = parts.join(".");
    const today = new Date().toISOString().slice(0, 10);
    setVer(nv); setDate(today); save(nv, today);
  };
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-md border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.03] px-3 py-2" data-testid={`legal-meta-${docKey}`}>
      <label className="block text-xs">
        <span className="opacity-70">Version</span>
        <input value={ver} onChange={(e) => setVer(e.target.value)} onBlur={() => save(ver, date)} data-testid={`legal-version-${docKey}`}
          className="mt-1 w-24 rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1 text-sm" />
      </label>
      <label className="block text-xs">
        <span className="opacity-70">Last updated</span>
        <input type="date" value={date} onChange={(e) => { setDate(e.target.value); save(ver, e.target.value); }} data-testid={`legal-date-${docKey}`}
          className="mt-1 rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1 text-sm" />
      </label>
      <Button variant="ghost" onClick={bump} disabled={busy} data-testid={`legal-bump-${docKey}`}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Bump version
      </Button>
      {(() => {
        if (!date) return null;
        const days = Math.floor((Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24));
        if (days > 30) return (
          <span className="ms-1 inline-flex items-center gap-1 rounded-md bg-[#F1C21B]/15 px-2 py-0.5 text-[11px] text-[#8A6D0B] dark:text-[#F1C21B]" data-testid={`legal-stale-${docKey}`}>
            <AlertTriangle size={12} /> Effective date is {days} days old — bump the version if the text has changed.
          </span>
        );
        return null;
      })()}
      {note && <span className="text-xs opacity-60">{note}</span>}
    </div>
  );
}

const DEFAULT_HERO_IMAGES = [
  "https://images.unsplash.com/photo-1543710397-c21ee19e2b95?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  "https://images.pexels.com/photos/20562278/pexels-photo-20562278.jpeg?auto=compress&cs=tinysrgb&w=1600",
  "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  "https://images.pexels.com/photos/22912025/pexels-photo-22912025.jpeg?auto=compress&cs=tinysrgb&w=1600",
  "https://images.unsplash.com/photo-1759281944287-9173dc7018e7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
];

function HeroImagesManager({ branding, setMsg, onSaved }) {
  const [imgs, setImgs] = useState(branding.home_hero_images || []);
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef(null);
  const custom = imgs.length > 0;
  const shown = custom ? imgs : DEFAULT_HERO_IMAGES;

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (fileRef.current) fileRef.current.value = "";
    if (!files.length) return;
    const results = await Promise.all(files.map((f) => new Promise((res) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.onerror = () => res(null); r.readAsDataURL(f);
    })));
    setImgs([...imgs, ...results.filter(Boolean)].slice(0, 8));
  };
  const remove = (i) => setImgs(imgs.filter((_, x) => x !== i));
  const save = async () => {
    setBusy(true); setMsg("Saving hero images…");
    try {
      const { data } = await client.put("/branding/hero-images", { images: imgs });
      setImgs(data.home_hero_images); onSaved?.(data.home_hero_images);
      setMsg(`✓ Saved ${data.count} hero image(s). They are now live on the Home page.`);
    } catch (e) { setMsg(apiErr(e)); }
    finally { setBusy(false); }
  };
  const restore = async () => {
    setBusy(true); setMsg("Restoring default hero images…");
    try {
      await client.put("/branding/hero-images", { images: [] });
      setImgs([]); onSaved?.([]);
      setMsg("✓ Restored the built-in default hero images.");
    } catch (e) { setMsg(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-2 border-t border-black/10 dark:border-white/10 pt-3" data-testid="branding-hero-images">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">Hero images (rotating background)</span>
        {custom ? <Badge color="info">{imgs.length} custom</Badge> : <Badge color="neutral">built-in defaults</Badge>}
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="hero-images-input" />
        <Button variant="ghost" className="ms-auto" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="hero-images-upload"><Upload size={15} /> Upload images</Button>
        <Button onClick={save} disabled={busy} data-testid="hero-images-save">{busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save images</Button>
        {custom && <Button variant="ghost" onClick={restore} disabled={busy} data-testid="hero-images-restore">Restore defaults</Button>}
      </div>
      <p className="text-xs opacity-50">
        {custom
          ? "These are your uploaded hero images. Max 8, each resized to 1920px and stored privately. Remove any, add more, then Save. “Restore defaults” brings back the built-in stock set."
          : "Currently showing the built-in stock images (free-to-use Unsplash/Pexels — no payment or attribution required). Upload your own owned or licensed images to replace them (max 8)."}
      </p>
      <div className="flex flex-wrap gap-2" data-testid="hero-images-grid">
        {shown.map((src, i) => (
          <div key={i} className="relative">
            <img src={src} alt="" className="h-16 w-28 rounded-md border border-black/10 dark:border-white/10 object-cover" data-testid={`hero-image-thumb-${i}`} />
            {custom && <button onClick={() => remove(i)} data-testid={`hero-image-remove-${i}`}
              className="absolute -right-1.5 -top-1.5 rounded-full bg-[#DA1E28] p-0.5 text-white"><X size={12} /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}


// ---------------- Word-level diff for policy edits (feature 2) ----------------
// Classic LCS-based diff on whitespace-split tokens. Returns [{type:'eq'|'add'|'del', text}].
function wordDiff(oldText, newText) {
  const a = String(oldText || "").split(/(\s+)/);
  const b = String(newText || "").split(/(\s+)/);
  const n = a.length, m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { out.push({ type: "eq", text: a[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ type: "del", text: a[i] }); i++; }
    else { out.push({ type: "add", text: b[j] }); j++; }
  }
  while (i < n) { out.push({ type: "del", text: a[i++] }); }
  while (j < m) { out.push({ type: "add", text: b[j++] }); }
  return out;
}


function PolicyDiffModal({ open, onClose, label, oldText, newText, onConfirmSave }) {
  const [busy, setBusy] = useState(false);
  const diff = React.useMemo(() => wordDiff(oldText, newText), [oldText, newText]);
  const adds = diff.filter((p) => p.type === "add").length;
  const dels = diff.filter((p) => p.type === "del").length;
  const unchanged = !adds && !dels;
  const doSave = async () => {
    setBusy(true);
    try { await onConfirmSave?.(); onClose?.(); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={`Review changes — ${label}`} testid="policy-diff-modal" wide={true}>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="policy-diff-summary">
          <Badge color="success">+{adds} added</Badge>
          <Badge color="warn">−{dels} removed</Badge>
          {unchanged && <Badge color="neutral">No changes</Badge>}
          <span className="opacity-60 text-xs ms-auto">Green = new, red-strikethrough = removed. Unchanged text is dimmed.</span>
        </div>
        <div className="grid gap-3 sm:grid-cols-2" data-testid="policy-diff-panes">
          <div>
            <div className="mb-1 text-xs font-medium opacity-70">Current saved (before)</div>
            <div className="max-h-[50vh] overflow-auto whitespace-pre-wrap rounded-md border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.03] p-3 text-sm" data-testid="policy-diff-before">
              {oldText || <span className="opacity-40 italic">(empty)</span>}
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs font-medium opacity-70">After save (new)</div>
            <div className="max-h-[50vh] overflow-auto whitespace-pre-wrap rounded-md border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.03] p-3 text-sm" data-testid="policy-diff-after">
              {diff.map((p, i) => (
                p.type === "eq"
                  ? <span key={i} className="opacity-70">{p.text}</span>
                  : p.type === "add"
                    ? <span key={i} className="bg-[#24A148]/20 text-[#24A148] rounded-sm px-[1px]" data-testid={`policy-diff-add-${i}`}>{p.text}</span>
                    : <span key={i} className="bg-[#DA1E28]/20 text-[#DA1E28] line-through rounded-sm px-[1px]" data-testid={`policy-diff-del-${i}`}>{p.text}</span>
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} data-testid="policy-diff-cancel">Cancel</Button>
          <Button onClick={doSave} disabled={busy || unchanged} data-testid="policy-diff-confirm">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Confirm & save
          </Button>
        </div>
      </div>
    </Modal>
  );
}


function NotifyRunnersModal({ open, onClose, docKey, docLabel }) {
  const [aud, setAud] = useState(null);
  const [audience, setAudience] = useState("all");
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedTiers, setSelectedTiers] = useState([]);
  const [customNote, setCustomNote] = useState("");
  const [previewLang, setPreviewLang] = useState("ENG");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [useEffective, setUseEffective] = useState(false);
  const [history, setHistory] = useState({ sent: [], scheduled: [] });
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = React.useCallback(async () => {
    try {
      const { data } = await client.get(`/admin/policy-notify/history?doc=${docKey}`);
      setHistory(data);
    } catch (_e) { /* silent */ }
  }, [docKey]);

  useEffect(() => {
    if (!open) return;
    setResult(null); setErr("");
    client.get("/admin/policy-notify/audiences").then((r) => setAud(r.data)).catch((e) => setErr(apiErr(e)));
    loadHistory();
  }, [open, docKey, loadHistory]);

  const loadPreview = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await client.post("/admin/policy-notify/preview", {
        doc: docKey, lang: previewLang, custom_note: customNote });
      setPreview(data);
    } catch (e) { setErr(apiErr(e)); }
    finally { setBusy(false); }
  };

  const send = async () => {
    setSending(true); setErr(""); setResult(null);
    try {
      const payload = { doc: docKey, audience, custom_note: customNote };
      if (audience === "role") payload.roles = selectedRoles;
      if (audience === "tier") payload.tiers = selectedTiers;
      if (scheduleOn && scheduledAt) payload.scheduled_at = new Date(scheduledAt).toISOString();
      if (useEffective) payload.use_effective_date = true;
      const { data } = await client.post("/admin/policy-notify/send", payload);
      setResult(data);
      loadHistory();
    } catch (e) { setErr(apiErr(e)); }
    finally { setSending(false); }
  };

  const cancelScheduled = async (qid) => {
    try {
      await client.delete(`/admin/policy-notify/scheduled/${qid}`);
      loadHistory();
    } catch (e) { setErr(apiErr(e)); }
  };

  const recipientCount = () => {
    if (!aud) return "…";
    if (audience === "all") return aud.total;
    if (audience === "paying") return aud.paying;
    if (audience === "role") return `${selectedRoles.length} role(s) selected`;
    if (audience === "tier") return `${selectedTiers.length} tier(s) selected`;
    return "?";
  };

  return (
    <Modal open={open} onClose={onClose} title={`Notify runners — ${docLabel}`} testid="policy-notify-modal" wide={true}>
      <div className="space-y-4">
        {err && <div className="rounded-md border border-[#DA1E28]/40 bg-[#DA1E28]/10 px-3 py-2 text-sm text-[#DA1E28]" data-testid="policy-notify-error">{err}</div>}
        {result ? (
          <div className="rounded-md border border-[#24A148]/40 bg-[#24A148]/10 p-3 text-sm" data-testid="policy-notify-result">
            {result.scheduled ? (
              <>
                <div className="font-medium text-[#24A148]">✓ Scheduled for {new Date(result.scheduled_at).toLocaleString()}</div>
                <div className="mt-1 text-xs opacity-70">Will send automatically at the time above. Cancel from Send history below.</div>
              </>
            ) : (
              <>
                <div className="font-medium text-[#24A148]">✓ Sent to {result.sent} of {result.recipients} runners</div>
                <div className="mt-1 text-xs opacity-70">Version {result.version} · Effective {result.effective_date}</div>
                {result.by_lang && Object.keys(result.by_lang).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {Object.entries(result.by_lang).map(([lc, ct]) => (
                      <Badge key={lc} color="info" data-testid={`policy-notify-bylang-${lc}`}>{lc}: {ct}</Badge>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <>
            {/* Audience picker */}
            <div className="space-y-2" data-testid="policy-notify-audience">
              <div className="text-sm font-medium">1. Audience</div>
              <div className="grid gap-2 sm:grid-cols-2">
                {[
                  ["all", `All runners (${aud?.total ?? "…"})`, "Everyone you've onboarded."],
                  ["paying", `Paying subscribers only (${aud?.paying ?? "…"})`, "Active subscription or paid support tier."],
                  ["role", "Specific role(s)", "Pick one or more roles below."],
                  ["tier", "Specific support tier(s)", "Pick one or more tiers below."],
                ].map(([val, label, hint]) => (
                  <label key={val} className={`flex cursor-pointer flex-col rounded-md border p-2 text-sm ${audience === val ? "border-[var(--primary)] bg-[var(--primary)]/5" : "border-black/10 dark:border-white/10"}`}>
                    <span className="flex items-center gap-2">
                      <input type="radio" name="audience" value={val} checked={audience === val}
                        onChange={() => setAudience(val)} data-testid={`policy-notify-audience-${val}`} />
                      <span className="font-medium">{label}</span>
                    </span>
                    <span className="ms-5 text-xs opacity-60">{hint}</span>
                  </label>
                ))}
              </div>
              {audience === "role" && aud?.roles?.length > 0 && (
                <div className="ms-3 mt-1 flex flex-wrap gap-2" data-testid="policy-notify-role-list">
                  {aud.roles.map((r) => (
                    <label key={r} className="flex items-center gap-1 rounded-md border border-black/10 dark:border-white/10 px-2 py-1 text-xs">
                      <input type="checkbox" checked={selectedRoles.includes(r)} data-testid={`policy-notify-role-${r}`}
                        onChange={(e) => setSelectedRoles(e.target.checked ? [...selectedRoles, r] : selectedRoles.filter((x) => x !== r))} />
                      {r}
                    </label>
                  ))}
                </div>
              )}
              {audience === "tier" && aud?.tiers?.length > 0 && (
                <div className="ms-3 mt-1 flex flex-wrap gap-2" data-testid="policy-notify-tier-list">
                  {aud.tiers.map((t) => (
                    <label key={t} className="flex items-center gap-1 rounded-md border border-black/10 dark:border-white/10 px-2 py-1 text-xs">
                      <input type="checkbox" checked={selectedTiers.includes(t)} data-testid={`policy-notify-tier-${t}`}
                        onChange={(e) => setSelectedTiers(e.target.checked ? [...selectedTiers, t] : selectedTiers.filter((x) => x !== t))} />
                      {t}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Custom note */}
            <div className="space-y-1" data-testid="policy-notify-note-wrap">
              <div className="text-sm font-medium">2. Optional note (translated to each recipient's language)</div>
              <textarea value={customNote} onChange={(e) => setCustomNote(e.target.value)}
                data-testid="policy-notify-note" rows={2}
                placeholder="e.g. This update expands data-retention rules per new regulation."
                className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
            </div>

            {/* Preview */}
            <div className="space-y-1" data-testid="policy-notify-preview-wrap">
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-sm font-medium">3. Preview email</div>
                <select value={previewLang} onChange={(e) => { setPreviewLang(e.target.value); setPreview(null); }}
                  data-testid="policy-notify-preview-lang"
                  className="rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1 text-xs">
                  {LANGS.map((l) => <option key={l.code} value={l.code}>{l.code + " · " + l.native}</option>)}
                </select>
                <Button variant="ghost" onClick={loadPreview} disabled={busy} data-testid="policy-notify-preview-load">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Load preview
                </Button>
              </div>
              {preview && (
                <div className="rounded-md border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.03] p-3" data-testid="policy-notify-preview">
                  <div className="mb-2 text-xs opacity-70"><b>Subject:</b> {preview.subject}</div>
                  <div className="prose prose-sm max-w-none dark:prose-invert" dangerouslySetInnerHTML={{ __html: preview.html }} />
                </div>
              )}
            </div>

            {/* Schedule for later */}
            <div className="space-y-1 rounded-md border border-black/10 dark:border-white/10 p-2" data-testid="policy-notify-schedule">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" checked={scheduleOn} onChange={(e) => { setScheduleOn(e.target.checked); if (e.target.checked) setUseEffective(false); }}
                  data-testid="policy-notify-schedule-toggle" />
                4. Schedule for later (optional)
              </label>
              {scheduleOn && (
                <div className="ms-5 space-y-1">
                  <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)}
                    data-testid="policy-notify-schedule-at"
                    className="rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1 text-sm" />
                  <p className="text-xs opacity-60">Runners will receive the email automatically at this time. Useful when the policy has a future effective date.</p>
                </div>
              )}
              <label className="mt-2 flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" checked={useEffective} onChange={(e) => { setUseEffective(e.target.checked); if (e.target.checked) setScheduleOn(false); }}
                  data-testid="policy-notify-effective-toggle" />
                Or match this policy's effective date
              </label>
              {useEffective && (
                <p className="ms-5 text-xs opacity-60">Auto-schedules the notification for the effective date stamped on this policy (see version chip). If the effective date is already past, sends 5 minutes from now.</p>
              )}
            </div>

            {/* Send bar */}
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-black/10 dark:border-white/10 bg-black/[0.03] dark:bg-white/[0.05] px-3 py-2" data-testid="policy-notify-sendbar">
              <div className="text-sm">Will send to <b data-testid="policy-notify-recipient-count">{recipientCount()}</b> · in each runner's preferred language.</div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={onClose} data-testid="policy-notify-cancel">Cancel</Button>
                <Button onClick={send} disabled={sending || (scheduleOn && !scheduledAt)} data-testid="policy-notify-send">
                  {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                  {scheduleOn ? " Schedule send" : useEffective ? " Schedule for effective date" : " Send now"}
                </Button>
              </div>
            </div>
          </>
        )}

        {/* History / audit trail */}
        <div className="mt-3 border-t border-black/10 dark:border-white/10 pt-3" data-testid="policy-notify-history">
          <button type="button" className="flex items-center gap-2 text-xs font-medium opacity-70 hover:opacity-100"
            onClick={() => setShowHistory((v) => !v)} data-testid="policy-notify-history-toggle">
            <GitCompare size={13} /> {showHistory ? "Hide" : "Show"} send history ({history.sent.length + history.scheduled.length})
          </button>
          {showHistory && (
            <div className="mt-2 space-y-2 text-xs">
              {history.scheduled.length > 0 && (
                <div>
                  <div className="font-medium opacity-70 mb-1">Upcoming ({history.scheduled.length})</div>
                  <ul className="space-y-1">
                    {history.scheduled.map((s) => (
                      <li key={s.id} className="flex items-center justify-between gap-2 rounded-md border border-[#F1C21B]/40 bg-[#F1C21B]/10 p-2" data-testid={`policy-notify-scheduled-${s.id}`}>
                        <span>⏱ {new Date(s.scheduled_at).toLocaleString()} · audience={s.audience}{s.custom_note ? ` · "${s.custom_note.slice(0, 40)}"` : ""}</span>
                        <Button variant="ghost" onClick={() => cancelScheduled(s.id)} data-testid={`policy-notify-cancel-${s.id}`}>Cancel</Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {history.sent.length > 0 && (
                <div>
                  <div className="font-medium opacity-70 mb-1">Sent ({history.sent.length})</div>
                  <ul className="space-y-1">
                    {history.sent.slice(0, 10).map((h, i) => (
                      <li key={i} className="rounded-md border border-black/10 dark:border-white/10 p-2" data-testid={`policy-notify-sent-${i}`}>
                        ✓ {new Date(h.created_at).toLocaleString()} · v{h.version} · audience={h.audience} · <b>{h.sent}/{h.recipients}</b>{h.by_lang ? ` · ${Object.entries(h.by_lang).map(([l,c]) => `${l}:${c}`).join(", ")}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {history.sent.length === 0 && history.scheduled.length === 0 && (
                <div className="opacity-60 italic">No sends yet for this policy.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}


function FooterPreviewModal({ open, onClose, branding }) {
  const links = [
    ["Website URL", branding?.contact_url, "contact_url"],
    ["Privacy Policy URL", branding?.privacy_policy_url, "privacy_policy_url"],
    ["Long Blog (SaaS) URL", branding?.long_blog_url, "long_blog_url"],
  ];
  return (
    <Modal open={open} onClose={onClose} title="Landing-page footer preview" testid="footer-preview-modal" wide={true}>
      <div className="space-y-3">
        <p className="text-xs opacity-60">
          This is exactly how the public landing footer renders with your current branding — including all legal links. Click any link to confirm it opens the right page.
        </p>
        {/* Rendered footer preview */}
        <div className="rounded-md border border-black/10 dark:border-white/10 bg-white/70 dark:bg-neutral-900/70 px-5 py-6 text-center text-xs opacity-90 backdrop-blur-sm" data-testid="footer-preview-render">
          <div className="mx-auto max-w-3xl space-y-1">
            <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 font-medium opacity-80">
              <a href="/about" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--primary)]" data-testid="footer-preview-about">About</a>
              <span className="opacity-40">·</span>
              <a href="/solutions" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--primary)]">Solutions</a>
              <span className="opacity-40">·</span>
              <a href="/reviews" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--primary)]">Reviews</a>
              <span className="opacity-40">·</span>
              <a href="/contact" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--primary)]">Contact</a>
              <span className="opacity-40">·</span>
              <a href="/backup" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--primary)]" data-testid="footer-preview-backup">Backup &amp; Recovery Policy</a>
              <span className="opacity-40">·</span>
              <a href="/cookies" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--primary)]" data-testid="footer-preview-cookies">Cookies Policy</a>
            </div>
            <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 font-medium opacity-80">
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--primary)]" data-testid="footer-preview-privacy">Privacy Policy</a>
              <span className="opacity-40">·</span>
              <a href="/eula" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--primary)]">EULA</a>
              <span className="opacity-40">·</span>
              <a href="/copyright" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--primary)]">Copyright Policy</a>
              <span className="opacity-40">·</span>
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--primary)]">Terms of Use</a>
            </div>
            <div className="opacity-70">{branding?.copyright_notice || "Copyright GCA 2026  All Rights Reserved"}</div>
          </div>
        </div>

        {/* External URL health check */}
        <div className="space-y-1" data-testid="footer-preview-linkcheck">
          <div className="text-sm font-medium">External URL configuration</div>
          <ul className="text-sm">
            {links.map(([label, url, key]) => (
              <li key={key} className="flex items-center gap-2 py-1" data-testid={`footer-link-${key}`}>
                <span className="w-40 opacity-70">{label}:</span>
                {url
                  ? <a href={url} target="_blank" rel="noopener noreferrer" className="truncate text-[var(--primary)] hover:underline">{url}</a>
                  : <Badge color="warn">not set</Badge>}
              </li>
            ))}
          </ul>
          <p className="text-xs opacity-50">Tip: leave URLs blank to hide them from the footer. Save branding after changes to publish.</p>
        </div>

        <div className="flex justify-end">
          <Button variant="ghost" onClick={onClose} data-testid="footer-preview-close">Close</Button>
        </div>
      </div>
    </Modal>
  );
}



function LegalContentEditor({ field, label, help, value, onChange, onSaved, setMsg, docKey, meta, onMetaSaved, seedFromDoc, currentSaved }) {
  const [lang, setLang] = useState("ENG");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const fileRef = React.useRef(null);
  const map = value || {};
  const translate = async () => {
    const base = (map.ENG || "").trim();
    if (!base) { setMsg("Enter the English text first, then auto-translate."); return; }
    setBusy(true); setMsg(`Translating “${label}” to all 15 languages…`);
    try {
      await onSaved();
      const { data } = await client.post("/branding/content/translate", { field, base_text: base });
      onChange(data[field]);
      setMsg(`✓ “${label}” translated into ${data.languages.length} languages.`);
    } catch (e) { setMsg(apiErr(e)); }
    finally { setBusy(false); }
  };
  const uploadDocx = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setUploading(true); setMsg(`Reading “${file.name}”…`);
    try {
      const fd = new FormData();
      fd.append("field", field); fd.append("file", file);
      const { data } = await client.post("/branding/content/upload-docx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const merged = { ...map, ENG: data.text };
      setLang("ENG");
      onChange(merged);
      // Persist immediately so the admin doesn't have to remember a separate Save.
      await client.put("/branding", { [field]: merged });
      setMsg(`✓ Imported & saved ${data.chars.toLocaleString()} characters from “${file.name}”. It’s now live in English — click Auto-translate for the other 14 languages.`);
    } catch (e2) { setMsg(apiErr(e2)); }
    finally { setUploading(false); }
  };
  const seed = async () => {
    if (!seedFromDoc) return;
    setSeeding(true); setMsg(`Loading current ${label} text from the User Manual…`);
    try {
      const { data } = await client.get(`/public/legal?doc=${encodeURIComponent(seedFromDoc)}&lang=ENG`);
      const text = (data && data.content) ? data.content : "";
      if (!text) { setMsg("No source text found in the manual for this policy yet."); return; }
      const merged = { ...map, ENG: text };
      setLang("ENG");
      onChange(merged);
      setMsg(`✓ Loaded ${text.length.toLocaleString()} characters from the User Manual — review, click “Save branding”, then Auto-translate.`);
    } catch (e) { setMsg(apiErr(e)); }
    finally { setSeeding(false); }
  };
  const engEmpty = !((map.ENG || "").trim());
  return (
    <div className="space-y-2" data-testid={`legal-editor-${field}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm opacity-70">{label}</span>
        <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid={`legal-lang-${field}`}
          className="ms-auto rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1 text-xs">
          {LANGS.map((l) => <option key={l.code} value={l.code}>{`${l.code} · ${l.native}${map[l.code] ? " ✓" : ""}`}</option>)}
        </select>
        {seedFromDoc && engEmpty ? (
          <Button variant="ghost" onClick={seed} disabled={seeding} data-testid={`legal-seed-${field}`}>
            {seeding ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Load current manual text
          </Button>
        ) : null}
        <input ref={fileRef} type="file" accept=".docx" className="hidden" onChange={uploadDocx} data-testid={`legal-docx-input-${field}`} />
        <Button variant="ghost" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`legal-docx-upload-${field}`}>
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload .docx
        </Button>
        <Button variant="ghost" onClick={translate} disabled={busy} data-testid={`legal-translate-${field}`}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Languages size={15} />} Auto-translate to 15 languages
        </Button>
        <Button variant="ghost" onClick={() => setDiffOpen(true)} disabled={!currentSaved} data-testid={`legal-diff-${field}`}>
          <GitCompare size={15} /> View diff
        </Button>
        {docKey && (
          <Button variant="ghost" onClick={() => setNotifyOpen(true)} data-testid={`legal-notify-${field}`}>
            <Send size={15} /> Notify runners
          </Button>
        )}
      </div>
      <textarea value={map[lang] || ""} data-testid={`legal-text-${field}`} rows={6}
        onChange={(e) => onChange({ ...map, [lang]: e.target.value })}
        placeholder={lang === "ENG" ? "Paste your English text here, or Upload .docx to import a Word document. Then Save and Auto-translate. Leave empty to hide." : `Translation for ${lang}. Use Auto-translate, or paste your own.`}
        className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
      {help && <span className="block text-xs opacity-50">{help}</span>}
      {docKey && <LegalMetaControls docKey={docKey} meta={meta} onMetaSaved={onMetaSaved} />}
      <PolicyDiffModal open={diffOpen} onClose={() => setDiffOpen(false)}
        label={label}
        oldText={((currentSaved || {})[lang] || (currentSaved || {}).ENG || "")}
        newText={(map[lang] || map.ENG || "")}
        onConfirmSave={onSaved} />
      {docKey && (
        <NotifyRunnersModal open={notifyOpen} onClose={() => setNotifyOpen(false)}
          docKey={docKey} docLabel={label} />
      )}
    </div>
  );
}


export default function Settings() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ SENDGRID_API_KEY: "", SENDER_EMAIL: "", TWILIO_ACCOUNT_SID: "", TWILIO_AUTH_TOKEN: "", TWILIO_PHONE_NUMBER: "", STRIPE_API_KEY: "" });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testMsg, setTestMsg] = useState("");
  const [testSmsTo, setTestSmsTo] = useState("");
  const [testSmsMsg, setTestSmsMsg] = useState("");
  const [sampleTo, setSampleTo] = useState("");
  const [sampleType, setSampleType] = useState("all");
  const [sampleMsg, setSampleMsg] = useState("");
  const [sampleBusy, setSampleBusy] = useState(false);
  const [quota, setQuota] = useState(null);
  const [quotaCap, setQuotaCap] = useState("");
  const [quotaMsg, setQuotaMsg] = useState("");
  const [prefs, setPrefs] = useState(null);
  const [prefsMsg, setPrefsMsg] = useState("");
  const [digest, setDigest] = useState(null);
  const [digestMsg, setDigestMsg] = useState("");
  const [branding, setBranding] = useState(null);
  const [savedBranding, setSavedBranding] = useState(null); // snapshot of last-persisted branding (for diff view)
  const [brandMsg, setBrandMsg] = useState("");
  const [footerPreviewOpen, setFooterPreviewOpen] = useState(false);
  const [pAck, setPAck] = useState(null);
  const [pAckMsg, setPAckMsg] = useState("");
  const [pAckBusy, setPAckBusy] = useState(false);
  const [pAckLog, setPAckLog] = useState(null);
  const [pAckLogOpen, setPAckLogOpen] = useState(false);
  const [legalMeta, setLegalMeta] = useState({});
  const onMetaSaved = (doc, m) => setLegalMeta((p) => ({ ...p, [doc]: m }));

  const [signup, setSignup] = useState(null);
  const [signupMsg, setSignupMsg] = useState("");
  useEffect(() => { client.get("/admin/signup-notify").then((r) => setSignup(r.data)).catch(() => {}); }, []);
  const saveSignup = async (patch) => {
    const next = { ...signup, ...patch };
    setSignup(next); setSignupMsg("Saving…");
    try {
      const { data } = await client.put("/admin/signup-notify", patch);
      setSignup(data); setSignupMsg("Saved.");
    } catch (e) { setSignupMsg(apiErr(e)); }
  };

  const [inbox, setInbox] = useState(null);
  const [inboxMsg, setInboxMsg] = useState("");
  useEffect(() => { client.get("/admin/inbox-notify").then((r) => setInbox(r.data)).catch(() => {}); }, []);
  const saveInbox = async (patch) => {
    setInbox((p) => ({ ...p, ...patch })); setInboxMsg("Saving…");
    try { const { data } = await client.put("/admin/inbox-notify", patch); setInbox(data); setInboxMsg("Saved."); }
    catch (e) { setInboxMsg(apiErr(e)); }
  };

  const SAMPLE_TYPES = [
    { value: "all", label: "All email types" },
    { value: "claim_code", label: "Activation / Claim code" },
    { value: "statement", label: "Activity statement" },
    { value: "security_alert", label: "Security alert" },
    { value: "campaign", label: "Marketing campaign" },
  ];

  const load = async () => {
    try { const { data } = await client.get("/settings/integrations"); setData(data); }
    catch (e) { setMsg(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { client.get("/admin/alert-prefs").then((r) => setPrefs(r.data.types)).catch(() => {}); }, []);
  useEffect(() => { client.get("/admin/alert-digest").then((r) => setDigest(r.data)).catch(() => {}); }, []);
  useEffect(() => { client.get("/branding").then((r) => { setBranding(r.data); setSavedBranding(r.data); }).catch(() => {}); }, []);
  useEffect(() => { client.get("/admin/policy-ack").then((r) => setPAck(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    client.get("/admin/legal-meta").then((r) => {
      const m = {};
      (r.data.items || []).forEach((it) => { m[it.doc] = { version: it.version, last_modified: it.last_modified }; });
      setLegalMeta(m);
    }).catch(() => {});
  }, []);
  const togglePolicy = (key) => setPAck((p) => {
    const has = (p.policies || []).includes(key);
    return { ...p, policies: has ? p.policies.filter((k) => k !== key) : [...(p.policies || []), key] };
  });
  const savePolicyAck = async () => {
    setPAckBusy(true); setPAckMsg("Saving…");
    try {
      const { data } = await client.put("/admin/policy-ack", {
        enabled: !!pAck.enabled, mode: pAck.mode || "accept",
        policies: pAck.policies || [], note: pAck.note || "" });
      setPAck(data);
      setPAckMsg(data.enabled
        ? (data.mode === "accept"
            ? "✓ Saved. Runners must review and accept the selected policies on their next visit."
            : "✓ Saved. Runners will see a dismissible notice about the selected policies.")
        : "✓ Saved. The policy prompt is turned off.");
    } catch (e) { setPAckMsg(apiErr(e)); }
    setPAckBusy(false);
  };
  const loadPAckLog = async () => {
    try { const { data } = await client.get("/admin/policy-ack/log"); setPAckLog(data); }
    catch (e) { setPAckLog({ items: [], total: 0 }); }
  };
  const togglePAckLog = () => {
    const next = !pAckLogOpen;
    setPAckLogOpen(next);
    if (next && !pAckLog) loadPAckLog();
  };
  const exportPAckLog = async () => {
    try {
      const res = await client.get("/admin/policy-ack/log.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `policy_acknowledgments_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setPAckMsg(apiErr(e)); }
  };
  const loadQuota = () => client.get("/settings/email-quota").then((r) => { setQuota(r.data); setQuotaCap(String(r.data.daily_cap)); }).catch(() => {});
  useEffect(() => { loadQuota(); }, []);
  const saveQuota = async () => {
    try {
      const { data } = await client.put("/settings/email-quota", { daily_cap: parseInt(quotaCap || "0", 10) });
      setQuota(data); setQuotaCap(String(data.daily_cap)); setQuotaMsg("Saved.");
    } catch (e) { setQuotaMsg("Could not save."); }
  };
  const flushQueue = async () => {
    setQuotaMsg("Flushing…");
    try { const { data } = await client.post("/settings/email-quota/flush"); setQuotaMsg(`Flushed ${data.flushed}; ${data.queued} still queued.`); loadQuota(); }
    catch (e) { setQuotaMsg("Flush failed."); }
  };

  const saveBranding = async () => {
    setBrandMsg("Saving…");
    try {
      const { data } = await client.put("/branding", branding);
      setBranding(data); setSavedBranding(data);
      // If the backend auto-bumped Cookies / Backup versions, refresh the meta chips.
      const bumped = data && data._autobumped;
      if (bumped && bumped.length > 0) {
        const m = { ...legalMeta };
        bumped.forEach((b) => { m[b.doc] = { version: b.version, last_modified: b.last_modified }; });
        setLegalMeta(m);
        setBrandMsg(`✓ Branding saved. Auto-bumped: ${bumped.map((b) => `${b.doc} → v${b.version} (${b.last_modified})`).join(", ")}.`);
      } else {
        setBrandMsg("✓ Branding & manual contact saved.");
      }
      return data;
    } catch (e) { setBrandMsg(apiErr(e)); }
  };

  const [packBusy, setPackBusy] = useState(false);
  const downloadTrademarkPack = async () => {
    setPackBusy(true); setBrandMsg("Building trademark specimen pack…");
    try {
      const res = await client.get("/site/admin/trademark-pack", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "FopFo_trademark_specimens.zip";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setBrandMsg("✓ Downloaded trademark specimen pack (6 marks + descriptions PDF).");
    } catch (e) { setBrandMsg(apiErr(e)); }
    finally { setPackBusy(false); }
  };

  const [packTmBusy, setPackTmBusy] = useState(false);
  const downloadTrademarkPackTm = async () => {
    setPackTmBusy(true); setBrandMsg("Building soft ™ trademark pack…");
    try {
      const res = await client.get("/site/admin/trademark-pack-tm", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "FopFo_trademark_specimens_soft_TM.zip";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setBrandMsg("✓ Downloaded soft ™ trademark pack (6 marks with a superscript ™ on each wordmark).");
    } catch (e) { setBrandMsg(apiErr(e)); }
    finally { setPackTmBusy(false); }
  };

  const [docBusy, setDocBusy] = useState(false);
  const [wpPass, setWpPass] = useState("");
  const [wpLog, setWpLog] = useState(null);
  const loadWpLog = async () => {
    try {
      const { data } = await client.get("/admin/whitepaper-access-log", { params: { limit: 50 } });
      setWpLog(data);
    } catch (e) { setBrandMsg(apiErr(e)); }
  };
  const downloadWhitepaper = async () => {
    if (!wpPass) { setBrandMsg("Enter your administrator password to unlock the whitepaper."); return; }
    setDocBusy(true); setBrandMsg("Verifying password & building the confidential whitepaper…");
    try {
      const res = await client.post("/admin/architecture-whitepaper.pdf", { password: wpPass }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "FopFo_Security_and_Architecture_Whitepaper.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setWpPass("");
      setBrandMsg("✓ Downloaded the confidential whitepaper. The PDF opens with your administrator password (NDA only).");
      if (wpLog) loadWpLog();
    } catch (e) {
      // Blob error responses need to be read as text before apiErr can parse them.
      let m = "Download failed.";
      try { m = JSON.parse(await e?.response?.data?.text?.() || "{}").detail || m; } catch { m = apiErr(e); }
      setBrandMsg(m);
    }
    finally { setDocBusy(false); }
  };

  const [compBusy, setCompBusy] = useState(false);
  const [ddCfg, setDdCfg] = useState({ legal_entity: "", report_period: "", auditor: "", effective_date: "", residency_region: "", incident_window: "" });
  useEffect(() => { client.get("/admin/dd-settings").then((r) => setDdCfg((p) => ({ ...p, ...(r.data || {}) }))).catch(() => {}); }, []);
  const saveDd = async () => {
    setCompBusy(true);
    try { await client.put("/admin/dd-settings", ddCfg); setBrandMsg("✓ Saved due-diligence details — they now fill into the PDF."); }
    catch (e) { setBrandMsg(apiErr(e)); }
    setCompBusy(false);
  };
  const downloadDueDiligence = async () => {
    setCompBusy(true); setBrandMsg("Building the vendor due-diligence response…");
    try {
      const res = await client.get("/admin/vendor-due-diligence.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Vendor_Due_Diligence_Response.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setBrandMsg("✓ Downloaded 'Vendor Due-Diligence Response'. Replace the bracketed placeholders before issuing.");
    } catch (e) {
      let m = "Download failed.";
      try { m = JSON.parse(await e?.response?.data?.text?.() || "{}").detail || m; } catch { m = apiErr(e); }
      setBrandMsg(m);
    }
    setCompBusy(false);
  };
  const downloadInvestmentDd = async () => {
    setCompBusy(true); setBrandMsg("Building the investment/consolidation/access due-diligence response…");
    try {
      const res = await client.get("/admin/investment-due-diligence.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Investment_Consolidation_Access_Due_Diligence.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setBrandMsg("✓ Downloaded 'Investment, Consolidation, Automation & Access Due-Diligence Response'.");
    } catch (e) {
      let m = "Download failed.";
      try { m = JSON.parse(await e?.response?.data?.text?.() || "{}").detail || m; } catch { m = apiErr(e); }
      setBrandMsg(m);
    }
    setCompBusy(false);
  };

  const downloadCompliance = async () => {    setCompBusy(true); setBrandMsg("Building the compliance overview…");
    try {
      const res = await client.get("/admin/compliance-report.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Enhanced_Compliance_in_View.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setBrandMsg("✓ Downloaded 'Enhanced Compliance in View'.");
    } catch (e) {
      let m = "Download failed.";
      try { m = JSON.parse(await e?.response?.data?.text?.() || "{}").detail || m; } catch { m = apiErr(e); }
      setBrandMsg(m);
    }
    finally { setCompBusy(false); }
  };

  const saveDigest = async (patch) => {
    const next = { ...digest, ...patch };
    setDigest(next);
    setDigestMsg("Saving…");
    try {
      const { data } = await client.put("/admin/alert-digest", patch);
      setDigest(data);
      setDigestMsg("Saved.");
    } catch (e) { setDigestMsg(apiErr(e)); }
  };

  const sendDigestNow = async () => {
    setDigestMsg("Sending…");
    try {
      const { data } = await client.post("/admin/alert-digest/send-now");
      setDigestMsg(data.delivery?.status === "sent" ? `✓ Digest sent (${data.total} alerts) via ${data.delivery.provider}.` : `Logged as fallback (${data.total} alerts) — add a SendGrid key to send live.`);
    } catch (e) { setDigestMsg(apiErr(e)); }
  };

  const togglePref = async (key) => {
    const next = prefs.map((p) => (p.key === key ? { ...p, muted: !p.muted } : p));
    setPrefs(next);
    setPrefsMsg("Saving…");
    try {
      const muted = next.filter((p) => p.muted).map((p) => p.key);
      const { data } = await client.put("/admin/alert-prefs", { muted });
      setPrefs(data.types);
      setPrefsMsg("Saved.");
    } catch (e) { setPrefsMsg(apiErr(e)); }
  };

  const save = async () => {
    setBusy(true); setMsg("Saving…");
    try {
      // only send non-empty fields so we never overwrite with blanks accidentally
      const payload = {};
      Object.entries(form).forEach(([k, v]) => { if (v.trim()) payload[k] = v.trim(); });
      const { data } = await client.put("/settings/integrations", payload);
      setData(data);
      setForm({ SENDGRID_API_KEY: "", SENDER_EMAIL: "", TWILIO_ACCOUNT_SID: "", TWILIO_AUTH_TOKEN: "", TWILIO_PHONE_NUMBER: "", STRIPE_API_KEY: "" });
      setMsg("Saved. New keys are now active.");
    } catch (e) { setMsg(apiErr(e)); }
    setBusy(false);
  };

  const sendTest = async () => {
    if (!testTo.trim()) { setTestMsg("Enter an email address."); return; }
    setTestMsg("Sending test…");
    try {
      const { data } = await client.post("/settings/integrations/test-email", { to: testTo.trim() });
      setTestMsg(data.status === "sent" ? `✓ Test email sent live via ${data.provider}.` : "Email not live yet — logged as fallback. Add a valid SendGrid key + sender.");
    } catch (e) { setTestMsg(apiErr(e)); }
  };

  const sendTestSms = async () => {
    if (!testSmsTo.trim()) { setTestSmsMsg("Enter a phone number (e.g. +1...)."); return; }
    setTestSmsMsg("Sending test SMS…");
    try {
      const { data } = await client.post("/settings/integrations/test-sms", { to: testSmsTo.trim() });
      setTestSmsMsg(data.status === "sent" ? `✓ Test SMS sent live via ${data.provider}.` : "SMS not live yet — logged as fallback. Check your Twilio credentials.");
    } catch (e) { setTestSmsMsg(apiErr(e)); }
  };

  const sendSamples = async () => {
    if (!sampleTo.trim()) { setSampleMsg("Enter an email address to receive the sample(s)."); return; }
    setSampleBusy(true); setSampleMsg("Sending sample(s)…");
    try {
      const { data } = await client.post("/settings/integrations/sample-emails", { to: sampleTo.trim(), type: sampleType });
      const live = data.results.filter((r) => r.status === "sent").length;
      const provider = data.results[0]?.provider || "fallback";
      setSampleMsg(live > 0
        ? `✓ Sent ${live}/${data.sent} sample email(s) live via ${provider}.`
        : `Logged ${data.sent} sample(s) as fallback — add a valid SendGrid key + sender to send live.`);
    } catch (e) { setSampleMsg(apiErr(e)); }
    setSampleBusy(false);
  };

  const statusBadge = (s) => s === "live"
    ? <Badge color="success"><CheckCircle2 size={12} className="inline" /> Live</Badge>
    : <Badge color="warning"><AlertTriangle size={12} className="inline" /> Fallback (logging only)</Badge>;

  return (
    <div className="space-y-8 fadeup" data-testid="settings-page">
      <div>
        <h1 className="font-head text-4xl font-light">Settings &amp; Integrations</h1>
        <p className="opacity-60">Configure delivery providers. Keys are stored securely and take effect immediately — no redeploy needed.</p>
      </div>

      <Card className="space-y-4" data-testid="settings-policy-ack">
        <div className="flex items-center gap-2">
          <FileCheck size={18} className="text-[var(--primary)]" />
          <span className="font-head text-lg">Policy update prompt for Runners</span>
        </div>
        <p className="text-sm opacity-60">
          When on, Runners are prompted about the selected policies on their next visit. Choose <b>Notify only</b> for small
          changes (a dismissible notice) or <b>Require review &amp; accept</b> for material changes (Runners are blocked from
          the applications until they review and accept each selected policy). Saving while the prompt is on re-issues it to
          every Runner, including those who acknowledged a previous update.
        </p>

        {!pAck ? <p className="text-sm opacity-50">Loading…</p> : (
          <>
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={!!pAck.enabled}
                onChange={(e) => setPAck({ ...pAck, enabled: e.target.checked })} data-testid="policy-ack-enabled" />
              Turn the policy prompt on
            </label>

            <div className={pAck.enabled ? "space-y-4" : "space-y-4 pointer-events-none opacity-50"}>
              <div>
                <div className="text-sm font-medium mb-1.5">Change type</div>
                <div className="flex flex-wrap gap-2">
                  {[["notify", "Notify only (dismissible)"], ["accept", "Require review & accept (blocks access)"]].map(([val, label]) => (
                    <button key={val} type="button" onClick={() => setPAck({ ...pAck, mode: val })}
                      data-testid={`policy-ack-mode-${val}`}
                      className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${(pAck.mode || "accept") === val ? "border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)] font-medium" : "border-black/15 dark:border-white/15"}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-sm font-medium mb-1.5">Policies to {(pAck.mode || "accept") === "accept" ? "require acceptance of" : "highlight"}</div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {(pAck.catalog || []).map((c) => (
                    <label key={c.key} className="flex items-center gap-2 rounded-lg border border-black/10 px-3 py-2 text-sm dark:border-white/10"
                      data-testid={`policy-ack-pick-${c.key}`}>
                      <input type="checkbox" checked={(pAck.policies || []).includes(c.key)}
                        onChange={() => togglePolicy(c.key)} data-testid={`policy-ack-pick-input-${c.key}`} />
                      {c.title}
                    </label>
                  ))}
                </div>
              </div>

              <label className="block text-sm">
                <span className="opacity-70">Message shown to Runners (optional)</span>
                <textarea value={pAck.note || ""} onChange={(e) => setPAck({ ...pAck, note: e.target.value })}
                  rows={2} maxLength={400} data-testid="policy-ack-note"
                  placeholder="e.g. We've clarified how backups are retained. Please review the updated policy."
                  className="mt-1 w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
              </label>
            </div>

            <div className="flex items-center gap-3">
              <Button onClick={savePolicyAck} disabled={pAckBusy} data-testid="policy-ack-save">
                {pAckBusy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save &amp; apply
              </Button>
              {pAck.issued_at && pAck.enabled && (
                <span className="text-xs opacity-50">Last issued {String(pAck.issued_at).slice(0, 16).replace("T", " ")} UTC</span>
              )}
            </div>
            {pAckMsg && <div className="text-sm opacity-80" data-testid="policy-ack-msg">{pAckMsg}</div>}

            <div className="border-t border-black/10 pt-3 dark:border-white/10">
              <div className="flex flex-wrap items-center gap-4">
                <button type="button" onClick={togglePAckLog} data-testid="policy-ack-log-toggle"
                  className="text-sm font-medium text-[var(--primary)] hover:underline">
                  {pAckLogOpen ? "Hide" : "View"} acknowledgment log{pAckLog ? ` (${pAckLog.total})` : ""}
                </button>
                <button type="button" onClick={exportPAckLog} data-testid="policy-ack-log-export"
                  className="inline-flex items-center gap-1 text-sm font-medium text-[var(--primary)] hover:underline">
                  <Download size={14} /> Export CSV
                </button>
              </div>
              {pAckLogOpen && (
                <div className="mt-3" data-testid="policy-ack-log">
                  {!pAckLog ? <p className="text-sm opacity-50">Loading…</p>
                    : pAckLog.items.length === 0 ? <p className="text-sm opacity-60">No acknowledgments recorded yet.</p>
                    : (
                    <div className="overflow-x-auto rounded-lg border border-black/10 dark:border-white/10">
                      <table className="w-full text-start text-xs">
                        <thead className="bg-black/5 dark:bg-white/5">
                          <tr>
                            <th className="px-3 py-2 font-medium">Runner</th>
                            <th className="px-3 py-2 font-medium">Policies accepted</th>
                            <th className="px-3 py-2 font-medium">Type</th>
                            <th className="px-3 py-2 font-medium">Accepted (UTC)</th>
                            <th className="px-3 py-2 font-medium">IP</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pAckLog.items.map((r) => (
                            <tr key={r.id} className="border-t border-black/5 dark:border-white/5" data-testid={`policy-ack-log-row-${r.id}`}>
                              <td className="px-3 py-2">
                                <div className="font-medium">{r.name || "—"}</div>
                                <div className="opacity-60">{r.email}</div>
                              </td>
                              <td className="px-3 py-2">{(r.policy_titles || []).join(", ") || "—"}</td>
                              <td className="px-3 py-2">{r.mode === "accept" ? "Review & accept" : "Notice"}</td>
                              <td className="px-3 py-2">{String(r.accepted_at || "").slice(0, 19).replace("T", " ")}</td>
                              <td className="px-3 py-2 opacity-70">{r.ip || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </Card>

      <Card className="space-y-4" data-testid="settings-email">
        <div className="flex items-center gap-2">
          <Mail size={18} className="text-[var(--primary)]" />
          <span className="font-head text-lg">Email (SendGrid)</span>
          <span className="ms-auto">{data && statusBadge(data.email.status)}</span>
        </div>
        <p className="text-sm opacity-60">Used to deliver activation codes, statements, manual-update alerts and bulk/scheduled emails. Get a key at app.sendgrid.com → Settings → API Keys; the sender email must be a verified sender.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="SendGrid API key" type="password" placeholder="SG.xxxx (leave blank to keep current)"
            value={form.SENDGRID_API_KEY} onChange={(v) => setForm({ ...form, SENDGRID_API_KEY: v })} info={data?.email.fields.SENDGRID_API_KEY} testid="set-sendgrid-key" />
          <Field label="Sender email (verified)" placeholder="noreply@yourdomain.com"
            value={form.SENDER_EMAIL} onChange={(v) => setForm({ ...form, SENDER_EMAIL: v })} info={data?.email.fields.SENDER_EMAIL} testid="set-sender-email" />
        </div>
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-black/10 dark:border-white/10 p-3">
          <span className="text-xs font-medium opacity-70">Send a test email to:</span>
          <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" data-testid="set-test-to"
            className="flex-1 min-w-48 rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
          <Button variant="ghost" onClick={sendTest} data-testid="set-test-send"><Send size={14} /> Send test</Button>
          {testMsg && <span className="w-full text-xs opacity-70" data-testid="set-test-msg">{testMsg}</span>}
        </div>
        <div className="space-y-2 rounded-md border border-black/10 dark:border-white/10 p-3" data-testid="set-sample-block">
          <span className="text-xs font-medium opacity-70">Preview what recipients receive — send yourself a sample email:</span>
          <div className="flex flex-wrap items-center gap-2">
            <select value={sampleType} onChange={(e) => setSampleType(e.target.value)} data-testid="set-sample-type"
              className="rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm outline-none focus:border-[var(--primary)]">
              {SAMPLE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <input value={sampleTo} onChange={(e) => setSampleTo(e.target.value)} placeholder="you@example.com" data-testid="set-sample-to"
              className="flex-1 min-w-48 rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
            <Button variant="ghost" onClick={sendSamples} disabled={sampleBusy} data-testid="set-sample-send"><Send size={14} /> Send sample</Button>
          </div>
          {sampleMsg && <span className="block text-xs opacity-70" data-testid="set-sample-msg">{sampleMsg}</span>}
        </div>
      </Card>

      <Card className="space-y-4" data-testid="settings-email-quota">
        <div className="flex items-center gap-2">
          <Bell size={18} className="text-[var(--primary)]" />
          <span className="font-head text-lg">Email quota guard</span>
          {quota && <span className="ms-auto" data-testid="quota-badge"><Badge color={quota.enabled ? "success" : "neutral"}>{quota.enabled ? "Active" : "Disabled"}</Badge></span>}
        </div>
        <p className="text-sm opacity-60">
          Protects your provider's daily send limit. Once the cap is reached, <b>marketing</b> emails are held in a queue and
          sent automatically when quota frees up, while <b>transactional</b> messages (codes, receipts, alerts) always go out.
          Set the cap to <b>0</b> to disable the guard.
        </p>
        {quota && (
          <div className="flex flex-wrap items-end gap-4 text-sm">
            <div>
              <label className="block text-xs opacity-70 mb-1">Daily cap</label>
              <input type="number" min="0" value={quotaCap} onChange={(e) => setQuotaCap(e.target.value)} data-testid="quota-cap-input"
                className="w-28 rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 outline-none focus:border-[var(--primary)]" />
            </div>
            <div className="opacity-80" data-testid="quota-stats">
              Sent today: <b>{quota.sent_today}</b>
              {quota.remaining !== null && <> · Remaining: <b>{quota.remaining}</b></>}
              {" "}· Queued: <b data-testid="quota-queued">{quota.queued}</b>
            </div>
            <Button variant="ghost" onClick={saveQuota} data-testid="quota-save"><Save size={14} /> Save cap</Button>
            <Button variant="ghost" onClick={flushQueue} data-testid="quota-flush" disabled={!quota.queued}><Send size={14} /> Flush queue now</Button>
            {quotaMsg && <span className="w-full text-xs opacity-70" data-testid="quota-msg">{quotaMsg}</span>}
          </div>
        )}
      </Card>

      <Card className="space-y-4" data-testid="settings-sms">
        <div className="flex items-center gap-2">
          <MessageSquare size={18} className="text-[var(--primary)]" />
          <span className="font-head text-lg">SMS (Twilio)</span>
          <span className="ms-auto">{data && statusBadge(data.sms.status)}</span>
        </div>
        <p className="text-sm opacity-60">Used to deliver the first 10 code characters and SMS notifications. Find these in your Twilio console.</p>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Account SID" type="password" placeholder="ACxxxx"
            value={form.TWILIO_ACCOUNT_SID} onChange={(v) => setForm({ ...form, TWILIO_ACCOUNT_SID: v })} info={data?.sms.fields.TWILIO_ACCOUNT_SID} testid="set-twilio-sid" />
          <Field label="Auth token" type="password" placeholder="••••"
            value={form.TWILIO_AUTH_TOKEN} onChange={(v) => setForm({ ...form, TWILIO_AUTH_TOKEN: v })} info={data?.sms.fields.TWILIO_AUTH_TOKEN} testid="set-twilio-token" />
          <Field label="From phone number" placeholder="+1..."
            value={form.TWILIO_PHONE_NUMBER} onChange={(v) => setForm({ ...form, TWILIO_PHONE_NUMBER: v })} info={data?.sms.fields.TWILIO_PHONE_NUMBER} testid="set-twilio-from" />
        </div>
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-black/10 dark:border-white/10 p-3">
          <span className="text-xs font-medium opacity-70">Send a test SMS to:</span>
          <input value={testSmsTo} onChange={(e) => setTestSmsTo(e.target.value)} placeholder="+15551234567" data-testid="set-test-sms-to"
            className="flex-1 min-w-48 rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
          <Button variant="ghost" onClick={sendTestSms} data-testid="set-test-sms-send"><Send size={14} /> Send test SMS</Button>
          {testSmsMsg && <span className="w-full text-xs opacity-70" data-testid="set-test-sms-msg">{testSmsMsg}</span>}
        </div>
      </Card>

      <Card className="space-y-4" data-testid="settings-payments">
        <div className="flex items-center gap-2">
          <CreditCard size={18} className="text-[var(--primary)]" />
          <span className="font-head text-lg">Payments (Stripe)</span>
          <span className="ms-auto">{data && statusBadge(data.payments.status)}</span>
        </div>
        <p className="text-sm opacity-60">Used for license checkout. Use your Stripe secret key (starts with sk_…). Find it at dashboard.stripe.com → Developers → API keys.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Stripe secret key" type="password" placeholder="sk_live_… or sk_test_… (leave blank to keep current)"
            value={form.STRIPE_API_KEY} onChange={(v) => setForm({ ...form, STRIPE_API_KEY: v })} info={data?.payments.fields.STRIPE_API_KEY} testid="set-stripe-key" />
        </div>
      </Card>

      {msg && <div className="rounded-md border border-[var(--primary)]/40 px-3 py-2 text-sm" data-testid="settings-msg">{msg}</div>}
      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} data-testid="settings-save"><Save size={15} /> Save integrations</Button>
      </div>

      <Card className="space-y-4" data-testid="settings-alert-prefs">
        <div className="flex items-center gap-2">
          <Bell size={18} className="text-[var(--primary)]" />
          <span className="font-head text-lg">Notification preferences</span>
          {prefsMsg && <span className="ms-auto text-xs opacity-60" data-testid="alert-prefs-msg">{prefsMsg}</span>}
        </div>
        <p className="text-sm opacity-60">Choose which system alert types appear in your notifications bell and System alerts log. Muting a type also stops its alert emails.</p>
        <div className="divide-y divide-black/5 dark:divide-white/5">
          {(prefs || []).map((p) => (
            <div key={p.key} className="flex items-center justify-between py-3" data-testid={`alert-pref-${p.key}`}>
              <span className="text-sm">{p.label}</span>
              <button onClick={() => togglePref(p.key)} data-testid={`alert-pref-toggle-${p.key}`}
                role="switch" aria-checked={!p.muted}
                className={`relative h-6 w-11 flex-shrink-0 rounded-full transition-colors ${p.muted ? "bg-black/15 dark:bg-white/15" : "bg-[var(--primary)]"}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${p.muted ? "left-0.5" : "left-[22px]"}`} />
              </button>
            </div>
          ))}
        </div>
        <p className="text-xs opacity-50">On = you'll be notified · Off = muted</p>

        <div className="mt-2 border-t border-black/10 dark:border-white/10 pt-4" data-testid="settings-alert-digest">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Alert digest email</span>
            {digestMsg && <span className="ms-auto text-xs opacity-60" data-testid="digest-msg">{digestMsg}</span>}
          </div>
          <p className="mt-1 text-sm opacity-60">Get one tidy recap email of all system alerts (grouped by type). Muted types are excluded.</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="digest-enabled" checked={!!digest?.enabled}
                onChange={(e) => saveDigest({ enabled: e.target.checked })} />
              Enable digest
            </label>
            <select value={digest?.frequency || "daily"} data-testid="digest-frequency"
              onChange={(e) => saveDigest({ frequency: e.target.value })}
              className="rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1.5 text-sm">
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
            {digest?.frequency === "weekly" && (
              <select value={digest?.weekday ?? 0} data-testid="digest-weekday"
                onChange={(e) => saveDigest({ weekday: Number(e.target.value) })}
                className="rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1.5 text-sm">
                {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            )}
            <label className="flex items-center gap-2 text-sm">at
              <select value={digest?.hour ?? 8} data-testid="digest-hour"
                onChange={(e) => saveDigest({ hour: Number(e.target.value) })}
                className="rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1.5 text-sm">
                {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{`${String(h).padStart(2, "0")}:00 UTC`}</option>)}
              </select>
            </label>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input type="email" value={digest?.recipient || ""} placeholder="recipient@email.com" data-testid="digest-recipient"
              onChange={(e) => setDigest({ ...digest, recipient: e.target.value })}
              onBlur={(e) => saveDigest({ recipient: e.target.value })}
              className="flex-1 min-w-48 rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
            <Button variant="ghost" onClick={sendDigestNow} data-testid="digest-send-now"><Send size={14} /> Send digest now</Button>
          </div>
          {digest?.last_sent && <p className="mt-2 text-xs opacity-40">Last sent: {String(digest.last_sent).slice(0, 16).replace("T", " ")} UTC</p>}
        </div>
      </Card>

      <Card className="space-y-4" data-testid="settings-inbox-notify">
        <div className="flex items-center gap-2">
          <Bell size={18} className="text-[var(--primary)]" />
          <span className="font-head text-lg">New app-message notifications</span>
          {inboxMsg && <span className="ms-auto text-xs opacity-60" data-testid="inbox-notify-msg">{inboxMsg}</span>}
        </div>
        <p className="text-sm opacity-60">Get an email the moment a visitor sends a message through the app's contact form, so you know to log in and view it.</p>
        {!inbox ? <p className="text-sm opacity-50">Loading…</p> : (
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={!!inbox.enabled} data-testid="inbox-notify-enabled"
                onChange={(e) => saveInbox({ enabled: e.target.checked })} />
              Email me on every new app message
            </label>
            <label className="flex items-center gap-2 text-sm flex-1 min-w-64">
              <span className="opacity-70">Notify:</span>
              <input type="email" value={inbox.email || ""} placeholder="you@example.com" data-testid="inbox-notify-email"
                onChange={(e) => setInbox({ ...inbox, email: e.target.value })}
                onBlur={(e) => saveInbox({ email: e.target.value })}
                className="flex-1 rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm outline-none focus:border-[var(--primary)]" />
            </label>
          </div>
        )}
      </Card>


      <Card className="space-y-4" data-testid="settings-signup-notify">
        <div className="flex items-center gap-2">
          <Bell size={18} className="text-[var(--primary)]" />
          <span className="font-head text-lg">New sign-up notifications</span>
          {signupMsg && <span className="ms-auto text-xs opacity-60" data-testid="signup-notify-msg">{signupMsg}</span>}
        </div>
        <p className="text-sm opacity-60">Get an email each time a new account registers on the suite. Useful for keeping an eye on organic sign-ups.</p>
        {!signup ? <p className="text-sm opacity-50">Loading…</p> : (
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={!!signup.enabled} data-testid="signup-notify-enabled"
                onChange={(e) => saveSignup({ enabled: e.target.checked })} />
              Email me on every new sign-up
            </label>
            <label className="flex items-center gap-2 text-sm flex-1 min-w-64">
              <span className="opacity-70">Notify:</span>
              <input type="email" value={signup.email || ""} placeholder="you@example.com" data-testid="signup-notify-email"
                onChange={(e) => setSignup({ ...signup, email: e.target.value })}
                onBlur={(e) => saveSignup({ email: e.target.value })}
                className="flex-1 rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm outline-none focus:border-[var(--primary)]" />
            </label>
          </div>
        )}
      </Card>


      <Card className="space-y-3" data-testid="settings-branding">
        <div className="flex flex-wrap items-center gap-2">
          <div className="font-head text-lg">Branding &amp; manual contact</div>
          {branding && (
            <Button variant="ghost" className="ms-auto" onClick={() => setFooterPreviewOpen(true)} data-testid="branding-footer-preview">
              <Eye size={15} /> Preview footer
            </Button>
          )}
        </div>
        <p className="text-sm opacity-60">Shown on the landing/login pages and printed on every User Manual (first &amp; last page, plus the Contact Us section). All fields are editable.</p>
        {branding && (
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ["suite_name", "Software suite name (header)"],
              ["suite_tagline", "Suite tagline (second line)"],
              ["copyright_notice", "Copyright notice (footer)"],
              ["contact_url", "Website URL (landing page)"],
              ["contact_email", "Backup contact email"],
              ["contact_tollfree", "Toll-free contact number"],
              ["privacy_policy_url", "Privacy Policy URL"],
              ["long_blog_url", "Long Blog (SaaS) URL — shown on the Blog page"],
            ].map(([k, label]) => (
              <label key={k} className="block text-sm">
                <span className="opacity-70">{label}</span>
                <input value={branding[k] || ""} data-testid={`branding-${k}`}
                  onChange={(e) => setBranding({ ...branding, [k]: e.target.value })}
                  className="mt-1 w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
              </label>
            ))}
          </div>
        )}
        {branding && (
          <div className="space-y-4 border-t border-black/10 dark:border-white/10 pt-3" data-testid="branding-home-hero">
            <div className="text-sm font-medium">Home page hero text (shown over the rotating pictures)</div>
            <p className="text-xs opacity-50">The large heading is the “Software suite name” above. These two fields are the tagline and the lead paragraph beneath it. Type the English text, Save, then Auto-translate to all 15 languages. Leave empty to use the built-in default.</p>
            <LegalContentEditor field="home_hero_tagline" label="Hero tagline (line under the heading)"
              help="e.g. “your Full Option Family Office”. Shown in the visitor’s language, English fallback."
              value={branding.home_hero_tagline} setMsg={setBrandMsg} onSaved={saveBranding}
              onChange={(v) => setBranding({ ...branding, home_hero_tagline: v })} />
            <LegalContentEditor field="home_hero_lead" label="Hero lead paragraph"
              help="The short welcoming paragraph under the tagline. Shown in the visitor’s language, English fallback."
              value={branding.home_hero_lead} setMsg={setBrandMsg} onSaved={saveBranding}
              onChange={(v) => setBranding({ ...branding, home_hero_lead: v })} />
            <div>
              <Button variant="ghost" data-testid="branding-hero-preview"
                onClick={() => {
                  localStorage.setItem("hero_preview", JSON.stringify({
                    suite_name: branding.suite_name || "",
                    tagline: branding.home_hero_tagline || {},
                    lead: branding.home_hero_lead || {},
                  }));
                  window.open("/?hero_preview=1", "_blank", "noopener");
                }}>
                <ExternalLink size={15} /> Preview on Home page
              </Button>
              <p className="mt-1 text-xs opacity-50">Opens the landing page in a new tab showing your current — even unsaved — hero text. Nothing is published until you click “Save branding”.</p>
            </div>
            <HeroImagesManager branding={branding} setMsg={setBrandMsg}
              onSaved={(list) => setBranding({ ...branding, home_hero_images: list })} />
          </div>
        )}
        {branding && (
          <div className="space-y-4 border-t border-black/10 dark:border-white/10 pt-3" data-testid="branding-legal">
            <div className="text-sm font-medium">Legal &amp; consent (localized, shown to users in their language)</div>
            <LegalContentEditor field="privacy_policy_content" label="Privacy Policy content"
              help="Shown as an inline “Privacy Policy” toggle at the base of the landing page. The URL above (if set) is offered as a “View full policy →” link."
              value={branding.privacy_policy_content} currentSaved={savedBranding?.privacy_policy_content} setMsg={setBrandMsg} onSaved={saveBranding}
              docKey="privacy" meta={legalMeta.privacy} onMetaSaved={onMetaSaved}
              onChange={(v) => setBranding({ ...branding, privacy_policy_content: v })} />
            <LegalContentEditor field="terms_content" label="Terms of Service content"
              help="Shown as an inline “Terms of Service” toggle at the base of the landing page (visitor’s language, English fallback)."
              value={branding.terms_content} currentSaved={savedBranding?.terms_content} setMsg={setBrandMsg} onSaved={saveBranding}
              docKey="terms" meta={legalMeta.terms} onMetaSaved={onMetaSaved}
              onChange={(v) => setBranding({ ...branding, terms_content: v })} />
            <LegalContentEditor field="eula_content" label="EULA content"
              help="End User Licence Agreement for the native apps. Upload your Word .docx, review, Save, then Auto-translate. Shown at /eula."
              value={branding.eula_content} currentSaved={savedBranding?.eula_content} setMsg={setBrandMsg} onSaved={saveBranding}
              docKey="eula" meta={legalMeta.eula} onMetaSaved={onMetaSaved}
              onChange={(v) => setBranding({ ...branding, eula_content: v })} />
            <LegalContentEditor field="copyright_content" label="Copyright Policy content"
              help="Shown at /copyright. Upload your Word .docx, review, Save, then Auto-translate."
              value={branding.copyright_content} currentSaved={savedBranding?.copyright_content} setMsg={setBrandMsg} onSaved={saveBranding}
              docKey="copyright" meta={legalMeta.copyright} onMetaSaved={onMetaSaved}
              onChange={(v) => setBranding({ ...branding, copyright_content: v })} />
            <LegalContentEditor field="saas_content" label="SaaS Agreement content (e-signed by Runners)"
              help="The full SaaS Agreement each Runner reads and e-signs. Upload your Word .docx (long documents supported), review, Save, then Auto-translate."
              value={branding.saas_content} currentSaved={savedBranding?.saas_content} setMsg={setBrandMsg} onSaved={saveBranding}
              docKey="saas_agreement" meta={legalMeta.saas_agreement} onMetaSaved={onMetaSaved}
              onChange={(v) => setBranding({ ...branding, saas_content: v })} />
            <div className="space-y-2" data-testid="legal-meta-extra">
              <div className="text-sm font-medium">Cookies &amp; Backup policies (editable, translatable — same as the other policies)</div>
              <p className="text-xs opacity-50">Upload a .docx, or click “Load current manual text” to pre-fill the English editor from the User Manual’s authoritative text. Then click “Save branding” and “Auto-translate to 15 languages”. Version &amp; date are auto-bumped on every save when the English text changes.</p>
              <LegalContentEditor field="cookies_content" label="Cookies Policy content"
                help="Shown at /cookies. Upload your Word .docx, review, Save, then Auto-translate. Version auto-bumps on save."
                value={branding.cookies_content} currentSaved={savedBranding?.cookies_content} setMsg={setBrandMsg} onSaved={saveBranding}
                docKey="cookies" meta={legalMeta.cookies} onMetaSaved={onMetaSaved}
                seedFromDoc="cookies"
                onChange={(v) => setBranding({ ...branding, cookies_content: v })} />
              <LegalContentEditor field="backup_content" label="Backup &amp; Recovery Policy content"
                help="Shown at /backup. Upload your Word .docx, review, Save, then Auto-translate. Version auto-bumps on save."
                value={branding.backup_content} currentSaved={savedBranding?.backup_content} setMsg={setBrandMsg} onSaved={saveBranding}
                docKey="backup" meta={legalMeta.backup} onMetaSaved={onMetaSaved}
                seedFromDoc="backup"
                onChange={(v) => setBranding({ ...branding, backup_content: v })} />
            </div>
            <LegalContentEditor field="system_consent_content" label="System-message consent statement"
              help="Replaces the default system-message consent checkbox text in the Master Enabler (visitor’s language, English fallback)."
              value={branding.system_consent_content} setMsg={setBrandMsg} onSaved={saveBranding}
              onChange={(v) => setBranding({ ...branding, system_consent_content: v })} />
            <label className="block text-sm" data-testid="branding-trademark-owner-wrap">
              <span className="opacity-70">Trademark owner (used in the auto-generated IP notice)</span>
              <input value={branding.trademark_owner || ""} data-testid="branding-trademark_owner"
                onChange={(e) => setBranding({ ...branding, trademark_owner: e.target.value })}
                placeholder="e.g. GCA"
                className="mt-1 w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
            </label>
            <LegalContentEditor field="trademark_notice" label="Trademark & IP notice"
              help="Auto-generated in English from the trademark owner and the six brand marks, and shown in the footer + PDF manuals. Use Auto-translate for the other 11 languages, or edit any language’s text."
              value={branding.trademark_notice} setMsg={setBrandMsg} onSaved={saveBranding}
              onChange={(v) => setBranding({ ...branding, trademark_notice: v })} />
            <div className="rounded-md border border-black/10 dark:border-white/10 p-3" data-testid="trademark-pack-block">
              <div className="text-sm font-medium">Trademark specimen pack</div>
              <p className="mt-1 mb-3 text-xs opacity-60">Download a ZIP of all six brand marks (FopFo shield, SEBAA, Currencida, BoxAlong, Familigi, FIDUX) on a clean background, plus the marks &amp; descriptions PDF — ready for filings and store listings.</p>
              <Button variant="ghost" onClick={downloadTrademarkPack} disabled={packBusy} data-testid="trademark-pack-download">
                {packBusy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download specimen pack (.zip)
              </Button>
              <p className="mt-4 mb-2 text-xs opacity-60">Or download the <b>soft ™ pack</b> — the same six brand marks with a small <b>superscript ™</b> added to the top-right of each wordmark (a black ™ beside the SEBAA bar), for use where a claimed-mark specimen is required.</p>
              <Button variant="ghost" onClick={downloadTrademarkPackTm} disabled={packTmBusy} data-testid="trademark-pack-tm-download">
                {packTmBusy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download soft ™ pack (.zip)
              </Button>
            </div>
            <div className="rounded-md border border-black/10 dark:border-white/10 p-3" data-testid="docs-packs-block">
              <div className="text-sm font-medium">Specifications &amp; documentation packs</div>
              <p className="mt-1 mb-3 text-xs opacity-60">Two ready-to-share documents. The <b>public pack</b> (feature spec + high-level security posture) is safe to hand anyone. The <b>whitepaper</b> holds deep technical/architecture detail and is <b>confidential — share only under NDA</b>. The whitepaper PDF is <b>password-protected with your administrator password</b> (you must enter it to build the file, and again to open it).</p>
              <div className="flex flex-wrap items-end gap-2">
                <a href={`${API}/public/specifications.pdf`} target="_blank" rel="noopener noreferrer" data-testid="public-spec-download">
                  <Button variant="ghost"><Download size={15} /> Public specifications (.pdf)</Button>
                </a>
                <div className="flex items-end gap-2">
                  <label className="block text-xs">
                    <span className="opacity-70">Your admin password</span>
                    <input type="password" value={wpPass} onChange={(e) => setWpPass(e.target.value)}
                      data-testid="whitepaper-password" placeholder="Password to lock/open the PDF"
                      className="mt-1 w-56 rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
                  </label>
                  <Button variant="ghost" onClick={downloadWhitepaper} disabled={docBusy} data-testid="whitepaper-download">
                    {docBusy ? <Loader2 size={15} className="animate-spin" /> : <Lock size={15} />} Architecture whitepaper — NDA (.pdf)
                  </Button>
                </div>
              </div>
              <div className="mt-3 border-t border-black/10 dark:border-white/10 pt-3" data-testid="compliance-doc-block">
                <div className="text-sm font-medium">Enhanced Compliance in View</div>
                <p className="mt-1 mb-2 text-xs opacity-60">ISO&nbsp;27001 &amp; SOC&nbsp;2 readiness overview — readiness snapshot, path-to-certification workflow, indicative timeline, a controls-in-place mapping and the Zero-Knowledge encryption scope. <b>No password required.</b></p>
                <Button variant="ghost" onClick={downloadCompliance} disabled={compBusy} data-testid="compliance-download">
                  {compBusy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Enhanced Compliance in View (.pdf)
                </Button>
              </div>
              <div className="mt-3 border-t border-black/10 dark:border-white/10 pt-3" data-testid="due-diligence-doc-block">
                <div className="text-sm font-medium">Vendor Due-Diligence Response</div>
                <p className="mt-1 mb-2 text-xs opacity-60">Q&amp;A answers to the SOC&nbsp;2 / security and payroll-benefits diligence questions — employee classification &amp; rate tables, paper/electronic timesheets, multi-state SUTA/FUTA, cross-app funding, and the six security items (SOC&nbsp;2, encryption, RBAC, residency, incident response, audit trail). The fields below fill into the PDF automatically. <b>No password required.</b></p>
                <div className="mb-2 grid gap-2 sm:grid-cols-2">
                  {[
                    ["legal_entity", "Legal entity name"],
                    ["auditor", "SOC 2 auditor (CPA firm)"],
                    ["report_period", "Report / observation period"],
                    ["effective_date", "Effective / report date"],
                    ["residency_region", "Data-residency region"],
                    ["incident_window", "Incident notification window"],
                  ].map(([f, label]) => (
                    <label key={f} className="text-xs">
                      <span className="opacity-70">{label}</span>
                      <input value={ddCfg[f] || ""} onChange={(e) => setDdCfg((p) => ({ ...p, [f]: e.target.value }))}
                        data-testid={`dd-field-${f}`} placeholder="(leave blank for placeholder)"
                        className="mt-0.5 w-full rounded border border-black/15 bg-transparent px-2 py-1 dark:border-white/15" />
                    </label>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="secondary" onClick={saveDd} disabled={compBusy} data-testid="dd-save">
                    {compBusy ? <Loader2 size={15} className="animate-spin" /> : null} Save details
                  </Button>
                  <Button variant="ghost" onClick={downloadDueDiligence} disabled={compBusy} data-testid="due-diligence-download">
                    {compBusy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Vendor Due-Diligence Response (.pdf)
                  </Button>
                  <Button variant="ghost" onClick={downloadInvestmentDd} disabled={compBusy} data-testid="investment-dd-download">
                    {compBusy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Investment / Consolidation / Access DD (.pdf)
                  </Button>
                </div>
              </div>
              <div className="mt-3 border-t border-black/10 dark:border-white/10 pt-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-medium opacity-80">Whitepaper access log {wpLog ? `(${wpLog.total})` : ""}</div>
                  <Button variant="ghost" onClick={loadWpLog} data-testid="whitepaper-log-load">
                    <Bell size={13} /> {wpLog ? "Refresh" : "View access log"}
                  </Button>
                </div>
                {wpLog && (
                  <div className="mt-2 max-h-48 overflow-auto rounded-md border border-black/10 dark:border-white/10" data-testid="whitepaper-log">
                    {(wpLog.items || []).length === 0 ? (
                      <p className="p-2 text-xs opacity-60">No downloads recorded yet.</p>
                    ) : (
                      <table className="w-full text-xs">
                        <thead className="opacity-60"><tr className="text-start">
                          <th className="p-2">When (UTC)</th><th className="p-2">Who</th><th className="p-2">IP</th>
                        </tr></thead>
                        <tbody>
                          {wpLog.items.map((r, i) => (
                            <tr key={i} className="border-t border-black/5 dark:border-white/5" data-testid={`whitepaper-log-row-${i}`}>
                              <td className="p-2 whitespace-nowrap">{(r.at || "").slice(0, 19).replace("T", " ")}</td>
                              <td className="p-2">{r.who}</td>
                              <td className="p-2">{r.ip || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {branding && (
          <div className="border-t border-black/10 dark:border-white/10 pt-3">
            <div className="text-sm font-medium">App Store &amp; Google Play links</div>
            <p className="mb-3 text-xs opacity-60">Paste each app's store URL once published. When empty, the Solutions page store buttons route visitors to the payment/contact portal instead.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                ["family_tree", "Family Tree"],
                ["holdings", "Holdings Management"],
                ["currency", "Currency Management"],
                ["benefits", "Benefits Management"],
              ].map(([app, label]) => {
                const sl = (branding.store_links || {})[app] || {};
                const set = (field, val) => setBranding({
                  ...branding,
                  store_links: { ...(branding.store_links || {}), [app]: { ...sl, [field]: val } },
                });
                return (
                  <div key={app} className="rounded-md border border-black/10 dark:border-white/10 p-3">
                    <div className="mb-2 text-sm font-medium">{label}</div>
                    <input value={sl.app_store || ""} placeholder="App Store URL (https://…)" data-testid={`store-${app}-app_store`}
                      onChange={(e) => set("app_store", e.target.value)}
                      className="mb-2 w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
                    <input value={sl.play_store || ""} placeholder="Google Play URL (https://…)" data-testid={`store-${app}-play_store`}
                      onChange={(e) => set("play_store", e.target.value)}
                      className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div className="flex items-center gap-3">
          <Button onClick={saveBranding} data-testid="branding-save"><Save size={15} /> Save branding</Button>
          {brandMsg && <span className="text-xs opacity-70" data-testid="branding-msg">{brandMsg}</span>}
        </div>
        <FooterPreviewModal open={footerPreviewOpen} onClose={() => setFooterPreviewOpen(false)} branding={branding} />
      </Card>

      <SaasSignableSetup setMsg={setBrandMsg} />
    </div>
  );
}
